/**
 * X10000 API Client
 *
 * 独立的 x10000 API 客户端，不依赖 isX10000Mode() 判断
 * 直接使用 x10000 后端服务
 */

import type {
  ApiError,
  NonceResponse,
  LoginRequest,
  LoginResponse,
  Market,
  Orderbook,
  Trade,
  Ticker,
  PriceResponse,
  FundingRate,
  FundingHistory,
  Position,
  Order,
  AccountBalance,
  CreateOrderRequest,
  BatchCancelRequest,
  BatchCancelResponse,
  ClosePositionRequest,
  CollateralRequest,
  TpSlRequest,
  TpSlResponse,
  TriggerOrdersResponse,
  LatestCandleResponse,
  KlinePeriod,
  GetCandlesParams,
  CreateReferralCodeParams,
  CreateReferralCodeResponse,
  BindReferralCodeParams,
  BindReferralCodeResponse,
  ReferralDashboardResponse,
  ReferralClaimSignatureRequest,
  ReferralClaimSignatureResponse,
  ClaimReferralResponse,
  OnChainDashboardResponse,
  ClaimableResponse,
  OperatorStatusResponse,
  WithdrawRecord,
  WithdrawRequest,
  WithdrawResponse,
  EarnDomainResponse,
  EarnProduct,
  EarnProductsResponse,
  EarnPerformanceResponse,
  EarnSubscription,
  EarnSubscriptionsResponse,
  EarnSubscribePrepareRequest,
  EarnSubscribePrepareResponse,
  PnlResponse,
  GetPnlParams,
} from "../types";

// 使用统一的后端 URL 配置
import { getX10000BackendUrl } from "config/backend";

const JWT_STORAGE_KEY_PREFIX = "renance_jwt_token";
const JWT_EXPIRY_KEY_PREFIX = "renance_jwt_expiry";
const LAST_ADDRESS_KEY = "renance_last_address";

// Helper function to get storage key for a specific address and chainId
function getStorageKey(address: string | null | undefined, keyPrefix: string, chainId?: number | null): string {
  if (!address || !chainId) {
    // Fallback to old key for backward compatibility (will be deprecated)
    return keyPrefix;
  }
  // Normalize address to lowercase for consistent key generation
  // Include chainId to separate tokens for different chains (testnet vs mainnet)
  const normalizedAddress = address.toLowerCase();
  return `${keyPrefix}_${chainId}_${normalizedAddress}`;
}

// Helper function to get the last used address from localStorage
export function getLastAddress(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(LAST_ADDRESS_KEY);
}

// Helper function to store the last used address
function setLastAddress(address: string | null): void {
  if (typeof window === "undefined") return;
  if (address) {
    localStorage.setItem(LAST_ADDRESS_KEY, address.toLowerCase());
  } else {
    localStorage.removeItem(LAST_ADDRESS_KEY);
  }
}

// ============================================
// Token Management
// ============================================
export function getStoredToken(address?: string | null, chainId?: number | null): string | null {
  if (typeof window === "undefined") return null;

  // If address is not provided, try to get from last used address
  let targetAddress = address;
  if (!targetAddress) {
    targetAddress = getLastAddress();
  }

  // Normalize address to lowercase for consistent lookup
  const normalizedAddress = targetAddress ? targetAddress.toLowerCase() : null;

  // First, try to get address and chainId-specific token (new format)
  if (normalizedAddress && chainId) {
    const storageKey = getStorageKey(normalizedAddress, JWT_STORAGE_KEY_PREFIX, chainId);
    const expiryKey = getStorageKey(normalizedAddress, JWT_EXPIRY_KEY_PREFIX, chainId);

    const token = localStorage.getItem(storageKey);
    const expiry = localStorage.getItem(expiryKey);

    if (token && expiry) {
      const expiryTime = parseInt(expiry, 10) * 1000;
      const now = Date.now();
      const isValid = now < expiryTime;

      // Only log if token is invalid (expired)
      if (!isValid) {
        console.warn("[getStoredToken] Token expired, clearing", {
          expiryTime,
          now,
          timeSinceExpiry: now - expiryTime,
          address: `${normalizedAddress.substring(0, 6)}...`,
          chainId,
        });
        clearStoredToken(normalizedAddress, chainId);
      }
      // Return valid token without logging (too frequent)

      if (isValid) {
        return token;
      }
    }
  }

  // Fallback: try to get legacy token (old format without address/chainId binding)
  // This provides backward compatibility for tokens stored before the address/chainId binding feature
  // Try address-only format first (without chainId)
  if (normalizedAddress) {
    const legacyAddressKey = getStorageKey(normalizedAddress, JWT_STORAGE_KEY_PREFIX, null);
    const legacyAddressExpiryKey = getStorageKey(normalizedAddress, JWT_EXPIRY_KEY_PREFIX, null);

    const legacyAddressToken = localStorage.getItem(legacyAddressKey);
    const legacyAddressExpiry = localStorage.getItem(legacyAddressExpiryKey);

    if (legacyAddressToken && legacyAddressExpiry) {
      if (Date.now() < parseInt(legacyAddressExpiry, 10) * 1000) {
        // If we have chainId, migrate the legacy token to address+chainId-specific format
        if (chainId) {
          setStoredToken(legacyAddressToken, parseInt(legacyAddressExpiry, 10), normalizedAddress, chainId);
          // Clear legacy token after migration
          localStorage.removeItem(legacyAddressKey);
          localStorage.removeItem(legacyAddressExpiryKey);
        }
        return legacyAddressToken;
      }
      // Legacy token expired, clear it
      localStorage.removeItem(legacyAddressKey);
      localStorage.removeItem(legacyAddressExpiryKey);
    }
  }

  // Fallback: try to get very old legacy token (without address and chainId)
  const legacyStorageKey = JWT_STORAGE_KEY_PREFIX;
  const legacyExpiryKey = JWT_EXPIRY_KEY_PREFIX;

  const legacyToken = localStorage.getItem(legacyStorageKey);
  const legacyExpiry = localStorage.getItem(legacyExpiryKey);

  if (legacyToken && legacyExpiry) {
    if (Date.now() < parseInt(legacyExpiry, 10) * 1000) {
      // If we have both address and chainId, migrate the legacy token to address+chainId-specific format
      if (normalizedAddress && chainId) {
        setStoredToken(legacyToken, parseInt(legacyExpiry, 10), normalizedAddress, chainId);
        // Clear legacy token after migration
        localStorage.removeItem(legacyStorageKey);
        localStorage.removeItem(legacyExpiryKey);
      }
      return legacyToken;
    }
    // Legacy token expired, clear it
    localStorage.removeItem(legacyStorageKey);
    localStorage.removeItem(legacyExpiryKey);
  }

  return null;
}

export function setStoredToken(token: string, expiresAt: number, address?: string | null, chainId?: number | null): void {
  if (typeof window === "undefined") return;

  if (!address || !chainId) {
    console.error(" setStoredToken called without address or chainId. Token will not be stored.", {
      address,
      chainId,
      hasToken: !!token,
    });
    return;
  }

  // Normalize address to lowercase for consistent storage
  const normalizedAddress = address.toLowerCase();

  // Store the address as the last used address
  setLastAddress(normalizedAddress);

  const storageKey = getStorageKey(normalizedAddress, JWT_STORAGE_KEY_PREFIX, chainId);
  const expiryKey = getStorageKey(normalizedAddress, JWT_EXPIRY_KEY_PREFIX, chainId);

  localStorage.setItem(storageKey, token);
  localStorage.setItem(expiryKey, expiresAt.toString());

  // Verify storage (only log if verification fails)
  const verifyToken = localStorage.getItem(storageKey);
  const verifyExpiry = localStorage.getItem(expiryKey);
  if (!verifyToken || !verifyExpiry) {
    console.error(" setStoredToken: Storage verification failed", {
      tokenStored: !!verifyToken,
      expiryStored: !!verifyExpiry,
      storageKey,
      expiryKey,
    });
  }

  // Notify token change listeners (same tab)
  // Use setTimeout to ensure localStorage is updated before event is dispatched
  setTimeout(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("x10000-token-change"));
    }
  }, 0);
}

export function clearStoredToken(address?: string | null, chainId?: number | null): void {
  if (typeof window === "undefined") return;

  // If address is not provided, clear the last used address's token
  let targetAddress = address;
  if (!targetAddress) {
    targetAddress = getLastAddress();
  }

  if (targetAddress && chainId) {
    // Clear chainId-specific token
    const storageKey = getStorageKey(targetAddress, JWT_STORAGE_KEY_PREFIX, chainId);
    const expiryKey = getStorageKey(targetAddress, JWT_EXPIRY_KEY_PREFIX, chainId);
    localStorage.removeItem(storageKey);
    localStorage.removeItem(expiryKey);
  }

  // Also clear legacy tokens (address-only, or no address/chainId)
  if (targetAddress) {
    // Clear address-only legacy token
    const legacyAddressKey = getStorageKey(targetAddress, JWT_STORAGE_KEY_PREFIX, null);
    const legacyAddressExpiryKey = getStorageKey(targetAddress, JWT_EXPIRY_KEY_PREFIX, null);
    localStorage.removeItem(legacyAddressKey);
    localStorage.removeItem(legacyAddressExpiryKey);
  }

  // Also clear the last address if clearing for a specific address
  if (address) {
    const lastAddress = getLastAddress();
    if (lastAddress && lastAddress.toLowerCase() === address.toLowerCase()) {
      setLastAddress(null);
    }
  }

  // Also clear very old legacy token (without address and chainId) for backward compatibility
  localStorage.removeItem(JWT_STORAGE_KEY_PREFIX);
  localStorage.removeItem(JWT_EXPIRY_KEY_PREFIX);

  // Notify token change listeners (same tab)
  setTimeout(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("x10000-token-change"));
    }
  }, 0);
}

export function isAuthenticated(address?: string | null, chainId?: number | null): boolean {
  return getStoredToken(address, chainId) !== null;
}


// ============================================
// Core Fetch Wrapper (X10000专用)
// ============================================
interface FetchOptions extends RequestInit {
  requireAuth?: boolean;
  address?: string | null; // Optional address for address-specific token lookup
}

async function apiFetch<T>(
  chainId: number,
  path: string,
  options: FetchOptions = {}
): Promise<T> {
  const baseUrl = getX10000BackendUrl(chainId);
  const url = `${baseUrl}/api/v1${path}`;

  // Debug log for K-line requests
  if (path.includes("/candles")) {
    // eslint-disable-next-line no-console
    console.log(` K-line request: ${url}`);
  }

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (options.requireAuth) {
    // Get token for the specified address and chainId
    // If address is not provided, try to get from last used address
    let targetAddress = options.address;
    if (!targetAddress) {
      targetAddress = getLastAddress();
    }

    const token = getStoredToken(targetAddress, chainId);
    if (!token) {
      console.error(" Token not found for authentication", {
        providedAddress: options.address,
        lastAddress: getLastAddress(),
        chainId,
      });
      throw new Error(" Authentication required. Please sign in again.");
    }

    // Log token usage for debugging
    console.log(" Using token for request", {
      address: targetAddress ? `${targetAddress.substring(0, 6)}...` : "unknown",
      chainId,
      tokenPrefix: token.substring(0, 20) + "...",
    });

    (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorData: ApiError;
      let errorText: string | undefined;
      try {
        errorData = await response.json();
        // Log the full error response for debugging
        console.error(` API Error (${response.status}):`, errorData);
      } catch (e) {
        // If JSON parsing fails, try to get text
        try {
          errorText = await response.text();
          console.error(` API Error (${response.status}) - Raw response:`, errorText);
        } catch (textError) {
          // Ignore text parsing errors
        }
        errorData = {
          error: "request_failed",
          message: errorText || `Request failed with status ${response.status}`,
        };
      }

      // Extract error message from various possible formats
      const fullMessage = errorData.message || errorData.error || (errorData as any).detail || errorText || "Request failed";

      // Handle 401 Unauthorized errors - token is invalid or expired
      if (response.status === 401) {
        // Get the address used for this request
        let targetAddress = options.address;
        if (!targetAddress) {
          targetAddress = getLastAddress();
        }
        // Only clear token if we have an address to clear it for
        if (targetAddress) {
          console.warn(" Clearing invalid/expired token for address and chainId");
          clearStoredToken(targetAddress, chainId);
        } else {
          console.warn(" Cannot clear token: no address available");
        }

        // Don't show error toast for 401, as it will trigger re-authentication
        // The calling code should handle re-authentication
      }
      // Note: We don't show toasts for API errors here because:
      // 1. Backend error messages may be in Chinese or other languages
      // 2. The t macro doesn't work correctly outside React components
      // 3. Components should handle error display with proper i18n support

      // Throw an Error object so it can be properly caught
      const error = new Error(fullMessage);
      (error as any).status = response.status;
      (error as any).errorData = errorData;
      throw error;
    }

    return response.json();
  } catch (error) {
    // Handle network errors and other exceptions

    // If error already has status (API error handled above), just re-throw
    if ((error as any)?.status) {
      throw error;
    }

    // For network errors without status, throw generic error
    // Note: We don't show toasts here because the t macro doesn't work correctly
    // outside React components. Components should handle error display.
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    throw new Error(errorMessage);
  }
}

// ============================================
// Auth API
// ============================================
export async function getNonce(chainId: number, address: string): Promise<NonceResponse> {
  return apiFetch<NonceResponse>(chainId, `/auth/nonce/${address}`);
}

export async function login(chainId: number, request: LoginRequest, address?: string | null): Promise<LoginResponse> {
  const response = await apiFetch<LoginResponse>(chainId, "/auth/login", {
    method: "POST",
    body: JSON.stringify(request),
  });

  // Store token with address and chainId binding
  const targetAddress = address || request.address;

  // Ensure expires_at is a number
  const expiresAt = typeof response.expires_at === 'string'
    ? parseInt(response.expires_at, 10)
    : response.expires_at;

  console.log(" login: About to store token", {
    hasToken: !!response.token,
    tokenPrefix: response.token ? `${response.token.substring(0, 20)}...` : "null",
    targetAddress: targetAddress ? `${targetAddress.substring(0, 6)}...` : "null",
    chainId,
    expiresAt,
    expiresAtType: typeof expiresAt,
  });

  if (!targetAddress || !chainId) {
    console.error(" login: Cannot store token - missing address or chainId", {
      targetAddress,
      chainId,
      address,
      requestAddress: request.address,
    });
  } else {
    setStoredToken(response.token, expiresAt, targetAddress, chainId);
    // Also store the last used address for token lookup when address is not provided
    setLastAddress(targetAddress);

    // Verify token was stored
    const verifyToken = getStoredToken(targetAddress, chainId);
    console.log(" login: Token storage verification", {
      stored: !!verifyToken,
      address: targetAddress ? `${targetAddress.substring(0, 6)}...` : "null",
      chainId,
    });
  }

  return response;
}

export function logout(address?: string | null, chainId?: number | null): void {
  clearStoredToken(address, chainId);
}

// ============================================
// Public Market API
// ============================================
export interface MarketsResponse {
  markets: Market[];
  total: number;
}

// Helper to convert symbol format (e.g., "BTC-USD" -> "BTCUSDT")
function convertSymbolToApiFormat(symbol: string): string {
  // If already in BTCUSDT format, return as is
  if (symbol.includes("USDT")) {
    return symbol.toUpperCase();
  }
  // Convert BTC-USD to BTCUSDT
  if (symbol.includes("-USD")) {
    return symbol.replace("-USD", "USDT").toUpperCase();
  }
  // If just BTC, append USDT
  return `${symbol}USDT`.toUpperCase();
}

export async function getMarkets(chainId: number, limit?: number): Promise<MarketsResponse> {
  const queryParams = limit ? `?limit=${limit}` : "";
  return apiFetch<MarketsResponse>(chainId, `/external/markets${queryParams}`);
}

export async function getOrderbook(chainId: number, symbol: string): Promise<Orderbook> {
  const apiSymbol = convertSymbolToApiFormat(symbol);
  return apiFetch<Orderbook>(chainId, `/external/markets/${apiSymbol}/orderbook`);
}

export interface TradesResponse {
  symbol: string;
  trades: Trade[];
}

export async function getTrades(chainId: number, symbol: string): Promise<TradesResponse> {
  const apiSymbol = convertSymbolToApiFormat(symbol);
  return apiFetch<TradesResponse>(chainId, `/external/markets/${apiSymbol}/trades`);
}

export async function getTicker(chainId: number, symbol: string): Promise<Ticker> {
  const apiSymbol = convertSymbolToApiFormat(symbol);
  return apiFetch<Ticker>(chainId, `/external/markets/${apiSymbol}/ticker`);
}

export async function getPrice(chainId: number, symbol: string): Promise<PriceResponse> {
  const apiSymbol = convertSymbolToApiFormat(symbol);
  return apiFetch<PriceResponse>(chainId, `/markets/${apiSymbol}/price`);
}

// ============================================
// Public Funding Rate API
// ============================================
export interface FundingRatesResponse {
  rates: FundingRate[];
}

export async function getAllFundingRates(chainId: number): Promise<FundingRatesResponse> {
  return apiFetch<FundingRatesResponse>(chainId, "/funding-rates");
}

export async function getFundingRate(chainId: number, symbol: string): Promise<FundingRate> {
  return apiFetch<FundingRate>(chainId, `/funding-rates/${symbol}`);
}

export async function getFundingHistory(
  chainId: number,
  symbol: string
): Promise<FundingHistory[]> {
  return apiFetch<FundingHistory[]>(chainId, `/funding-rates/${symbol}/history`);
}

// ============================================
// Protected Account API (Requires Auth)
// ============================================
export interface PositionsResponse {
  positions: Position[];
  total_unrealized_pnl: string;
  total_collateral: string;
}

export interface OrdersResponse {
  orders: Order[];
}

export interface BalancesResponse {
  balances: AccountBalance[];
}

export async function getPositions(chainId: number, address?: string | null): Promise<PositionsResponse> {
  return apiFetch<PositionsResponse>(chainId, "/account/positions", { requireAuth: true, address });
}

export async function getOrders(chainId: number, address?: string | null): Promise<OrdersResponse> {
  return apiFetch<OrdersResponse>(chainId, "/account/orders", { requireAuth: true, address });
}

export async function getTriggerOrders(
  chainId: number,
  address?: string | null
): Promise<TriggerOrdersResponse> {
  return apiFetch<TriggerOrdersResponse>(chainId, "/trigger-orders", { requireAuth: true, address });
}

export async function getBalances(chainId: number, address?: string | null): Promise<BalancesResponse> {
  return apiFetch<BalancesResponse>(chainId, "/account/balances", { requireAuth: true, address });
}

export interface AccountTradesResponse {
  trades: Trade[];
}

export async function getAccountTrades(chainId: number, address?: string | null): Promise<AccountTradesResponse> {
  return apiFetch<AccountTradesResponse>(chainId, "/account/trades", { requireAuth: true, address });
}

export interface WithdrawHistoryResponse {
  withdrawals: WithdrawRecord[];
}

export async function getWithdrawHistory(chainId: number): Promise<WithdrawHistoryResponse> {
  return apiFetch<WithdrawHistoryResponse>(chainId, "/withdraw/history", { requireAuth: true });
}

export async function requestWithdraw(chainId: number, request: WithdrawRequest): Promise<WithdrawResponse> {
  return apiFetch<WithdrawResponse>(chainId, "/withdraw/request", {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

export interface ConfirmWithdrawRequest {
  tx_hash: string;
}

export interface ConfirmWithdrawResponse {
  success: boolean;
  message?: string;
}

/**
 * Confirm withdrawal by submitting transaction hash
 * This is optional but recommended to speed up status updates
 */
export async function confirmWithdraw(
  chainId: number,
  withdrawId: string,
  request: ConfirmWithdrawRequest
): Promise<ConfirmWithdrawResponse> {
  return apiFetch<ConfirmWithdrawResponse>(chainId, `/withdraw/${withdrawId}/confirm`, {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

// ============================================
// Protected Order API (Requires Auth)
// ============================================
export interface CreateOrderResponse {
  order_id: string;
  status: string;
  filled_amount: string;
  remaining_amount: string;
  average_price: string | null;
  created_at: string;
}

export async function createOrder(
  chainId: number,
  request: CreateOrderRequest,
  address?: string | null
): Promise<CreateOrderResponse> {
  return apiFetch<CreateOrderResponse>(chainId, "/orders", {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
    address,
  });
}

export interface CancelOrderRequest {
  signature: string;
  timestamp: number;
}

export interface CancelOrderResponse {
  order_id: string;
  status: string;
  created_at: string;
}

export async function cancelOrder(
  chainId: number,
  orderId: string,
  request: CancelOrderRequest
): Promise<CancelOrderResponse> {
  return apiFetch<CancelOrderResponse>(chainId, `/orders/${orderId}`, {
    method: "DELETE",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

export async function batchCancelOrders(
  chainId: number,
  request: BatchCancelRequest
): Promise<BatchCancelResponse> {
  return apiFetch<BatchCancelResponse>(chainId, "/orders/batch", {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

// ============================================
// Protected Position API (Requires Auth)
// ============================================
export async function closePosition(
  chainId: number,
  positionId: string,
  request?: ClosePositionRequest
): Promise<CreateOrderResponse> {
  return apiFetch<CreateOrderResponse>(chainId, `/positions/${positionId}/close`, {
    method: "POST",
    body: JSON.stringify(request || {}),
    requireAuth: true,
  });
}

export async function addPositionCollateral(
  chainId: number,
  positionId: string,
  request: CollateralRequest
): Promise<Position> {
  return apiFetch<Position>(chainId, `/positions/${positionId}/collateral/add`, {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

export async function removePositionCollateral(
  chainId: number,
  positionId: string,
  request: CollateralRequest
): Promise<Position> {
  return apiFetch<Position>(chainId, `/positions/${positionId}/collateral/remove`, {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

export async function updatePositionCollateral(
  chainId: number,
  positionId: string,
  request: CollateralRequest
): Promise<Position> {
  return addPositionCollateral(chainId, positionId, request);
}

// ============================================
// Position TP/SL API
// ============================================

/**
 * Set Take Profit and Stop Loss for a position
 * POST /api/v1/positions/:position_id/tp-sl
 */
export async function setPositionTpSl(
  chainId: number,
  positionId: string,
  request: TpSlRequest
): Promise<TpSlResponse> {
  const response = await apiFetch<{ success: boolean; data: TpSlResponse }>(
    chainId,
    `/positions/${positionId}/tp-sl`,
    {
      method: "POST",
      body: JSON.stringify(request),
      requireAuth: true,
    }
  );
  return response.data;
}

/**
 * Get Take Profit and Stop Loss for a position
 * GET /api/v1/positions/:position_id/tp-sl
 */
export async function getPositionTpSl(
  chainId: number,
  positionId: string
): Promise<TpSlResponse> {
  const response = await apiFetch<{ success: boolean; data: TpSlResponse }>(
    chainId,
    `/positions/${positionId}/tp-sl`,
    {
      requireAuth: true,
    }
  );
  return response.data;
}

/**
 * Delete Take Profit and Stop Loss for a position
 * DELETE /api/v1/positions/:position_id/tp-sl
 */
export async function deletePositionTpSl(
  chainId: number,
  positionId: string
): Promise<{ success: boolean; data: string; error: string | null }> {
  return apiFetch<{ success: boolean; data: string; error: string | null }>(
    chainId,
    `/positions/${positionId}/tp-sl`,
    {
      method: "DELETE",
      requireAuth: true,
    }
  );
}

// ============================================
// K-line / Candles API
// ============================================
export interface Candle {
  /** Unix timestamp in milliseconds - period start time (API returns milliseconds) */
  time: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  quote_volume?: string;
  trade_count?: number;
  is_final?: boolean;
}

export interface CandlesResponse {
  symbol: string;
  period: string;
  candles: Candle[];
}

export interface LatestCandleResponse {
  symbol: string;
  period: string;
  candle: Candle;
  is_final: boolean;
}

export type KlinePeriod = "1m" | "5m" | "15m" | "30m" | "1h" | "4h" | "1d";

export interface GetCandlesParams {
  period: KlinePeriod;
  limit?: number;
  start?: number;
  end?: number;
}

export async function getCandles(
  chainId: number,
  symbol: string,
  params: GetCandlesParams
): Promise<CandlesResponse> {
  const apiSymbol = convertSymbolToApiFormat(symbol);
  const queryParams = new URLSearchParams();
  queryParams.set("period", params.period);
  if (params.limit !== undefined) queryParams.set("limit", params.limit.toString());
  // Convert milliseconds to seconds for backend API
  if (params.start !== undefined) queryParams.set("from", Math.floor(params.start / 1000).toString());
  if (params.end !== undefined) queryParams.set("to", Math.floor(params.end / 1000).toString());

  return apiFetch<CandlesResponse>(chainId, `/external/markets/${apiSymbol}/candles?${queryParams.toString()}`);
}

export async function getLatestCandle(
  chainId: number,
  symbol: string,
  period: KlinePeriod
): Promise<LatestCandleResponse> {
  return apiFetch<LatestCandleResponse>(chainId, `/klines/${symbol}/candles/latest?period=${period}`);
}

// ============================================
// Referral API (根据 API 文档: https://zdocs.tubex.chat/account/referral)
// ============================================

// EIP-712 Typed Data interface
/**
 * 创建推荐码
 * POST /api/v1/referral/codes
 */
export async function createReferralCode(
  chainId: number,
  params: CreateReferralCodeParams
): Promise<CreateReferralCodeResponse> {
  return apiFetch<CreateReferralCodeResponse>(chainId, "/referral/codes", {
    method: "POST",
    body: JSON.stringify(params),
    requireAuth: true,
  });
}

/**
 * 绑定推荐码
 * POST /api/v1/referral/bind
 */
export async function bindReferralCode(
  chainId: number,
  params: BindReferralCodeParams
): Promise<BindReferralCodeResponse> {
  return apiFetch<BindReferralCodeResponse>(chainId, "/referral/bind", {
    method: "POST",
    body: JSON.stringify(params),
    requireAuth: true,
  });
}

/**
 * 获取推荐面板
 * GET /api/v1/referral/dashboard
 */
export async function getReferralDashboard(chainId: number): Promise<ReferralDashboardResponse> {
  return apiFetch<ReferralDashboardResponse>(chainId, "/referral/dashboard", {
    requireAuth: true,
  });
}

/**
 * 获取领取返佣签名
 * POST /api/v1/referral/on-chain/claim-signature
 */
export async function getReferralClaimSignature(
  chainId: number,
  request: ReferralClaimSignatureRequest
): Promise<ReferralClaimSignatureResponse> {
  return apiFetch<ReferralClaimSignatureResponse>(chainId, "/referral/on-chain/claim-signature", {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
  });
}

/**
 * 领取奖励
 * POST /api/v1/referral/claim
 */
export async function claimReferralReward(chainId: number): Promise<ClaimReferralResponse> {
  return apiFetch<ClaimReferralResponse>(chainId, "/referral/claim", {
    method: "POST",
    requireAuth: true,
  });
}

/**
 * 获取链上面板（公开接口）
 * GET /api/v1/referral/on-chain/dashboard/:address
 */
export async function getOnChainReferralDashboard(
  chainId: number,
  address: string
): Promise<OnChainDashboardResponse> {
  return apiFetch<OnChainDashboardResponse>(chainId, `/referral/on-chain/dashboard/${address}`);
}

/**
 * 查询可领取金额（公开接口）
 * GET /api/v1/referral/on-chain/claimable/:address
 */
export async function getClaimableReferralAmount(
  chainId: number,
  address: string
): Promise<ClaimableResponse> {
  return apiFetch<ClaimableResponse>(chainId, `/referral/on-chain/claimable/${address}`);
}

/**
 * 操作员状态（公开接口）
 * GET /api/v1/referral/on-chain/operator-status
 */
export async function getReferralOperatorStatus(chainId: number): Promise<OperatorStatusResponse> {
  return apiFetch<OperatorStatusResponse>(chainId, "/referral/on-chain/operator-status");
}

// ============================================
// Earn API (理财服务)
// ============================================

/**
 * 获取 EIP-712 Domain 信息
 * GET /api/v1/earn/domain
 */
export async function getEarnDomain(chainId: number): Promise<EarnDomainResponse> {
  return apiFetch<EarnDomainResponse>(chainId, "/earn/domain");
}

/**
 * 获取产品列表
 * GET /api/v1/earn/products
 */
export async function getEarnProducts(
  chainId: number,
  params?: { status?: string; page?: number; page_size?: number }
): Promise<EarnProductsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set("status", params.status);
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.page_size) searchParams.set("page_size", String(params.page_size));

  const query = searchParams.toString();
  const path = query ? `/earn/products?${query}` : "/earn/products";
  return apiFetch<EarnProductsResponse>(chainId, path);
}

/**
 * 获取产品详情
 * GET /api/v1/earn/products/:id
 */
export async function getEarnProduct(chainId: number, productId: string): Promise<EarnProduct> {
  return apiFetch<EarnProduct>(chainId, `/earn/products/${productId}`);
}

/**
 * 获取历史表现
 * GET /api/v1/earn/performance
 */
export async function getEarnPerformance(
  chainId: number,
  limit?: number
): Promise<EarnPerformanceResponse> {
  const path = limit ? `/earn/performance?limit=${limit}` : "/earn/performance";
  return apiFetch<EarnPerformanceResponse>(chainId, path);
}

/**
 * 获取我的申购列表 (需要认证)
 * GET /api/v1/earn/subscriptions
 * 注意: API 返回原始数组，需要包装成 EarnSubscriptionsResponse 格式
 */
export async function getEarnSubscriptions(chainId: number, address?: string | null): Promise<EarnSubscriptionsResponse> {
  const subscriptions = await apiFetch<EarnSubscription[]>(chainId, "/earn/subscriptions", {
    requireAuth: true,
    address,
  });
  return { subscriptions };
}

/**
 * 准备申购 - 获取后端签名 (需要认证)
 * POST /api/v1/earn/subscribe/prepare
 */
export async function prepareEarnSubscribe(
  chainId: number,
  request: EarnSubscribePrepareRequest,
  address?: string | null
): Promise<EarnSubscribePrepareResponse> {
  return apiFetch<EarnSubscribePrepareResponse>(chainId, "/earn/subscribe/prepare", {
    method: "POST",
    body: JSON.stringify(request),
    requireAuth: true,
    address,
  });
}

// ============================================
// PnL API (每日和累计盈亏)
// ============================================

/**
 * 获取账户每日和累计盈亏数据
 * GET /api/v1/account/pnl
 */
export async function getAccountPnl(
  chainId: number,
  params?: GetPnlParams,
  address?: string | null
): Promise<PnlResponse> {
  const searchParams = new URLSearchParams();
  if (params?.symbol) searchParams.set("symbol", params.symbol);
  if (params?.start_date) searchParams.set("start_date", params.start_date);
  if (params?.end_date) searchParams.set("end_date", params.end_date);
  if (params?.days !== undefined) searchParams.set("days", String(params.days));

  const query = searchParams.toString();
  const path = query ? `/account/pnl?${query}` : "/account/pnl";

  return apiFetch<PnlResponse>(chainId, path, {
    requireAuth: true,
    address,
  });
}

